<template>
  <div class="chat-float-box" :class="{ show: showChat }">
    <div class="chat-header">
      HiOmics AI智能体
      <button class="close-btn" @click="closeDialog">×</button>
    </div>

    <!-- 聊天内容区域：绑定滚动事件 -->
    <div class="chat-content" ref="messageListRef" @scroll="handleScroll">
      <div style="color: #909399">我是HiOmics生信分析平台的AI助手，您可以在这里咨询工具操作问题、多组学相关知识、R代码生成、任务报错排查、分析结果解读，输入您的问题，现在开始吧！</div>
      <!-- 历史消息 -->
      <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.role]">
        <div class="avatar">{{ msg.role === 'user' ? '我' : 'AI' }}</div>
        <div class="msg-content">
          <!-- 用户消息：显示文本 + 附件 -->
          <template v-if="msg.role === 'user'">
            <div class="msg-text" v-if="msg.text" v-html="renderMarkdown(msg.text)"></div>
            <!-- 附件列表（历史消息中） -->
            <div class="attachment-list" v-if="msg.attachments && msg.attachments.length">
              <div 
                v-for="(att, i) in msg.attachments" 
                :key="i" 
                :class="['attachment-item', att.fileType]"
                @click="previewAttachment(att, i)"
              >
                <!-- 图片类型 -->
                <template v-if="att.fileType === 'image'">
                  <img :src="att.url" :alt="att.fileName" />
                </template>
                <!-- 其它文件类型 -->
                <template v-else>
                  <div class="file-card">
                    <div class="file-icon">
                      <svg viewBox="0 0 24 24" width="35" height="35" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                    </div>
                    <div class="file-info">
                      <div class="file-name" :title="att.fileName">{{ att.fileName }}</div>
                      <div class="file-size">{{ formatFileSize(att.fileSize) }}</div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <!-- AI消息：纯文本 -->
          <div class="msg-text" v-else v-html="renderMarkdown(msg.content)"></div>
        </div>
      </div>

      <!-- 流式输出 -->
      <div v-if="streaming" class="message assistant">
        <div class="avatar">AI</div>
        <div class="msg-content">
          <div class="msg-text" v-html="renderMarkdown(streamContent, true)"></div>
        </div>
      </div>
    </div>    

    <!-- 输入区域：支持文件上传（带进度） -->
    <div class="chat-input-area">
      <!-- 回到底部按钮 -->
      <button 
        v-show="showScrollToBottom || streaming" 
        class="scroll-to-bottom-btn" 
        :class="{ loading: streaming }"
        @click="scrollToBottom(true)"
        :title="streaming ? 'AI正在生成中...' : '回到底部'"
      >
        <template v-if="streaming">
          <div class="loading-ring-container">
            <div class="glow-ring"></div>
            <svg class="down-arrow" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7"/>
            </svg>
          </div>
        </template>
        <template v-else>
          <div class="arrow-container">
            <div class="arrow-glow"></div>
            <svg class="down-arrow" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7"/>
            </svg>
          </div>
        </template>
      </button>
      <div class="chat-editor">
        <!-- 已选文件预览（带进度） -->
        <div class="selected-files" v-if="selectedFiles.length > 0">
          <div 
            v-for="(file, index) in selectedFiles" 
            :key="index" 
            class="selected-file-item"
            :class="[file.fileType, { uploading: file.status === 'uploading', error: file.status === 'error', placeholder: file.status === 'pending' }]"
          >
            <!-- ========== 上传前占位符 ========== -->
            <template v-if="file.status === 'pending'">
              <div class="file-placeholder">
                <div class="placeholder-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="12" y1="8" x2="12" y2="16"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                </div>
                <div class="placeholder-name">{{ file.name }}</div>
                <div class="placeholder-hint">等待上传...</div>
              </div>
            </template>

            <!-- ========== 上传中 ========== -->
            <template v-else-if="file.status === 'uploading'">
              <!-- 图片类型：显示缩略图 + 进度遮罩 -->
              <template v-if="file.fileType === 'image'">
                <img :src="file.url" />
                <div class="upload-overlay">
                  <div class="progress-ring">
                    <svg viewBox="0 0 36 36">
                      <path class="bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                      <path 
                        class="progress" 
                        :stroke-dasharray="`${file.progress}, 100`"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" 
                      />
                    </svg>
                    <span class="progress-text">{{ file.progress }}%</span>
                  </div>
                </div>
              </template>
              <!-- 其它类型：文件卡片 + 进度条 -->
              <template v-else>
                <div class="file-card">
                  <div class="file-icon">
                    <svg viewBox="0 0 24 24" width="35" height="35" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </div>
                  <div class="file-info">
                    <div class="file-name" :title="file.name">{{ file.name }}</div>
                    <div class="file-progress-bar">
                      <div class="file-progress-fill" :style="{ width: file.progress + '%' }"></div>
                    </div>
                    <div class="file-progress-text">{{ file.progress }}%</div>
                  </div>
                </div>
              </template>
            </template>

            <!-- ========== 上传完成 ========== -->
            <template v-else-if="file.status === 'done'">
              <!-- 图片类型：显示图片 -->
              <template v-if="file.fileType === 'image'">
                <img :src="file.url" @click="previewImage([file], 0)" />
              </template>
              <!-- 其它类型：显示文件卡片 -->
              <template v-else>
                <div class="file-card" @click="downloadFile(file)">
                  <div class="file-icon">
                    <svg viewBox="0 0 24 24" width="35" height="35" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </div>
                  <div class="file-info">
                    <div class="file-name" :title="file.fileName || file.name">{{ file.fileName || file.name }}</div>
                    <div class="file-size">{{ formatFileSize(file.fileSize) }}</div>
                  </div>
                </div>
              </template>
            </template>

            <!-- ========== 上传失败 ========== -->
            <template v-else-if="file.status === 'error'">
              <div class="upload-overlay error">
                <span class="error-text">上传失败</span>
                <button class="retry-btn" @click.stop="retryUpload(index)">重试</button>
              </div>
            </template>

            <!-- 删除按钮 -->
            <span class="remove-btn" @click.stop="removeFile(index)">×</span>
          </div>
        </div>

        <div class="input-row">
          <div class="textarea-wrapper">
            <textarea
              v-model="inputMessage"
              placeholder="请输入您的问题..."
              @keydown="handleKeydown"
              rows="2"
              ref="inputRef"
            ></textarea>
          </div>
        </div>

        <div class="chat-editor-action">
          <!-- 上传按钮 -->
          <el-tooltip effect="dark" content="上传文件" placement="top">
            <div>
              <button class="upload-btn" @click.stop="triggerUpload" :disabled="streaming">
                <span class="plus-icon">+</span>
              </button>
              <input 
                type="file" 
                ref="fileInput" 
                style="display: none" 
                multiple 
                :accept="uploadAccept"
                @change="handleFileChange"
              />
            </div>
          </el-tooltip>
          <svg-icon v-if="streaming" @click="stopStream" type="stop" />
          <svg-icon 
            type="run" 
            v-else 
            @click="sendMessage" 
            :disabled="streaming || (!inputMessage.trim() && !selectedFiles.length) || hasUploading" 
          />
        </div>
      </div>
    </div>

    <!-- 全局图片预览器 -->
    <el-image-viewer
      v-if="previewVisible"
      :url-list="previewList"
      :initial-index="previewIndex"
      @close="previewVisible = false"
      hide-on-click-modal
    />
  </div>
</template>

<script setup>
  import { ref, computed, nextTick, onUnmounted, defineProps, onMounted, watch } from 'vue'
  import { createCozeStream } from '@/config/utils/sse'
  import { marked } from 'marked'
  import { useRouter } from 'vue-router'
  import { destroyChat } from "./Index.js"
  import { notice, clearLogin, cache } from "@/config"
  import store from "@/store"
  import { STATIC_URL } from "@/../public/config"
  import { getUploadConfig } from "@/api/Upload"

  const props = defineProps({
    dialogVisible: {
      type: Boolean,
      default: true
    }
  });

  const closeDialog = () => {
    showChat.value = false
    destroyChat()
  }

  const router = useRouter()
  const messages = ref([])
  const inputMessage = ref('')
  const streaming = ref(false)
  const streamContent = ref('')
  const messageListRef = ref(null)
  const inputRef = ref(null)
  const fileInput = ref(null)
  const selectedFiles = ref([])
  let currentStream = null
  let countdownTimer = null

  // ========== 【修改】上传配置 ==========
  const uploadFileExtMap = ref({})        // {".ext": ["mime1", "mime2"]} 映射对象
  const uploadFileMimeType = ref([])      // MIME 类型数组（去重后的值）
  const defaultUploadMaxFileSize = 1024 * 1024 * 10
  const uploadMaxFileSize = ref(defaultUploadMaxFileSize)
  const uploadAccept = ref('')

  // ========== 滚动控制相关 ==========
  const showScrollToBottom = ref(false)
  const hasAutoScrolled = ref(false)
  const isUserScrolledUp = ref(false)
  const SCROLL_THRESHOLD = 50

  const handleScroll = () => {
    const el = messageListRef.value
    if (!el) return
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const isNotAtBottom = distanceToBottom > SCROLL_THRESHOLD
    showScrollToBottom.value = isNotAtBottom
    if (isNotAtBottom) {
      isUserScrolledUp.value = true
    } else {
      isUserScrolledUp.value = false
    }
  }

  // ========== 图片预览相关 ==========
  const previewVisible = ref(false)
  const previewList = ref([])
  const previewIndex = ref(0)

  const previewImage = (files, index) => {
    previewList.value = files.map(f => f.url)
    previewIndex.value = index
    previewVisible.value = true
  }

  const previewAttachment = (att) => {
    if (att.fileType === 'image') {
      previewImage([att], 0)
    } else {
      downloadFile(att)
    }
  }

  const downloadFile = (file) => {
    if (file.url) {
      window.open(file.url, '_blank')
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    let i = 0
    while (bytes >= 1024 && i < units.length - 1) {
      bytes /= 1024
      i++
    }
    return bytes.toFixed(2) + ' ' + units[i]
  }

  // ========== 【修改】根据 {".ext": ["mime1", "mime2"]} 生成 accept 字符串 ==========
  const buildAcceptString = (mimeMap) => {
    const accepts = []
    
    // 添加所有 MIME 类型（去重）
    for (const mimeList of Object.values(mimeMap)) {
      for (const mime of mimeList) {
        if (!accepts.includes(mime)) {
          accepts.push(mime)
        }
      }
    }
    
    // 添加扩展名（去掉前面的点）
    for (const ext of Object.keys(mimeMap)) {
      accepts.push(ext)
    }
    
    return accepts.join(',')
  }

  // ========== 【修改】判断文件类型，基于扩展名映射 ==========
  const getFileType = (fileName, mimeType = '') => {
    const ext = '.' + (fileName.split('.').pop() || '').toLowerCase()
    
    // 从配置中查找该扩展名对应的 MIME 类型列表
    const expectedMimes = uploadFileExtMap.value[ext] || []
    
    // 图片类型判断
    const imageMimes = ['image/jpeg', 'image/png', 'image/bmp']
    
    // 检查文件 MIME 是否匹配配置中的图片类型
    const isImage = expectedMimes.some(mime => imageMimes.includes(mime)) || imageMimes.includes(mimeType)
    
    if (isImage) return 'image'
    return 'file'
  }

  const hasUploading = computed(() => {
    return selectedFiles.value.some(f => f.status === 'uploading')
  })

  // ========== 【修改】获取上传配置，适配 {".ext": ["mime1", "mime2"]} 格式 ==========
  const getUploadFileConfig = async () => {
    try {
      const { data } = await getUploadConfig()

      // 从接口获取配置：fileMimeType 是 {".ext": ["mime1", "mime2"]} 的对象
      const mimeMap = data?.fileMimeType ?? {
        '.jpeg': ['image/jpeg'],
        '.jpg': ['image/jpeg'],
        '.png': ['image/png'],
        '.bmp': ['image/bmp'],
        '.txt': ['text/plain'],
        '.csv': ['text/csv'],
        '.tsv': ['text/tab-separated-values'],
        '.pdf': ['application/pdf']
      }
      
      // 保存扩展名映射
      uploadFileExtMap.value = mimeMap
      
      // 提取所有 MIME 类型（扁平化去重）
      const allMimes = []
      for (const mimeList of Object.values(mimeMap)) {
        allMimes.push(...mimeList)
      }
      uploadFileMimeType.value = [...new Set(allMimes)]
      
      uploadMaxFileSize.value = data?.fileMaxSize ?? defaultUploadMaxFileSize
      
      // 生成 accept 字符串
      uploadAccept.value = buildAcceptString(mimeMap)
      
    } catch (error) {
      // 出错时使用默认值
      const defaultMimeMap = {
        '.jpeg': ['image/jpeg'],
        '.jpg': ['image/jpeg'],
        '.png': ['image/png'],
        '.bmp': ['image/bmp'],
        '.txt': ['text/plain'],
        '.csv': ['text/csv'],
        '.tsv': ['text/tab-separated-values'],
        '.pdf': ['application/pdf']
      }
      
      uploadFileExtMap.value = defaultMimeMap
      
      const allMimes = []
      for (const mimeList of Object.values(defaultMimeMap)) {
        allMimes.push(...mimeList)
      }
      uploadFileMimeType.value = [...new Set(allMimes)]
      
      uploadMaxFileSize.value = defaultUploadMaxFileSize
      uploadAccept.value = buildAcceptString(defaultMimeMap)
      // notice('错误', '获取上传文件配置失败，使用默认配置', 'error')
    }
  }

  onMounted(async () => {
    await getUploadFileConfig()
    await nextTick()
    inputRef.value?.focus()
    autoResizeTextarea()
  })

  onUnmounted(() => {
    if (countdownTimer) clearInterval(countdownTimer)
    selectedFiles.value.forEach(f => {
      if (f.xhr) f.xhr.abort()
    })
  })

  const showChat = ref(props.dialogVisible)

  const triggerUpload = () => {
    fileInput.value?.click()
  }

  // ========== 【修改】文件选择处理，基于扩展名映射校验（支持多 MIME） ==========
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    
    for (const file of files) {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase()
      
      // 通过扩展名查找是否在允许列表中
      const expectedMimes = uploadFileExtMap.value[ext]
      
      if (!expectedMimes || !expectedMimes.length) {
        const allowedExts = Object.keys(uploadFileExtMap.value).join('，')
        notice('提示', `不支持的文件类型：${ext}，仅支持：${allowedExts}`, 'error')
        continue
      }
      
      // 文件大小校验
      if (file.size > uploadMaxFileSize.value) {
        const maxSizeHuman = formatFileSize(uploadMaxFileSize.value)
        notice('提示', `文件：${file.name}，大小：${formatFileSize(file.size)}，最大允许上传：${maxSizeHuman}`, 'error')
        continue
      }
      
      // MIME 类型校验（检查文件实际 MIME 是否在允许的列表中）
      const fileMime = file.type
      if (fileMime && !expectedMimes.includes(fileMime)) {
        const mimeStr = expectedMimes.join(' 或 ')
        notice('提示', `文件格式 ${fileMime} 不匹配，${ext} 文件应为: ${mimeStr}`, 'error')
        continue
      }
      
      const fileType = getFileType(file.name, file.type)
      
      const fileItem = {
        file: file,
        url: null,
        name: file.name,
        fileType: fileType,
        fileSize: file.size,
        progress: 0,
        status: 'pending',
        fileId: null,
        xhr: null,
        fileName: file.name
      }
      
      if (fileType === 'image') {
        const reader = new FileReader()
        reader.onload = (e) => {
          fileItem.url = e.target.result
          selectedFiles.value.push(fileItem)
          const index = selectedFiles.value.length - 1
          setTimeout(() => uploadFileWithProgress(fileItem, index), 100)
        }
        reader.readAsDataURL(file)
      } else {
        selectedFiles.value.push(fileItem)
        const index = selectedFiles.value.length - 1
        setTimeout(() => uploadFileWithProgress(fileItem, index), 100)
      }
    }
    
    e.target.value = ''
  }

  const doReLogin = (msg) => {
    const countdownSeconds = 5
    let remaining = countdownSeconds
    const countdownMsg = {
      role: 'assistant',
      content: `${msg}，${remaining}秒后自动跳转登录页...`,
      isCountdown: true
    };
    messages.value.push(countdownMsg)
    countdownTimer = setInterval(() => {
      remaining--
      const msgIndex = messages.value.length - 1
      if (messages.value[msgIndex]?.isCountdown) {
        if (remaining > 0) {
          messages.value[msgIndex].content = `${msg}，${remaining}秒后自动跳转登录页...`
        } else {
          clearLogin()
          messages.value[msgIndex].content = `${msg}，正在跳转...`
          clearInterval(countdownTimer)
          countdownTimer = null
          router.push({ name: 'UserLogin'})
        }
      }
    }, 1000)
  }

  const tokenName = "Authorization-Token"
  const getToken = () => {
    return typeof (cache("userInfo")[tokenName]) === 'undefined' ? '' : cache("userInfo")[tokenName]
  }

  const uploadFileWithProgress = (fileItem, index) => {
    if (fileItem.status === 'uploading') return

    const formData = new FormData()
    formData.append('file', fileItem.file)

    updateFileStatus(index, { status: 'uploading', progress: 0 })

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      updateFileStatus(index, { xhr })

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100)
          updateFileStatus(index, { progress: percentComplete })
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText)

            if (result.status === 401) {
              doReLogin(result.message)
              reject(new Error(result.message))
              return
            }

            if (result.status !== 200) {
              notice('提示', result.message, 'error')
              updateFileStatus(index, { status: 'error', progress: 0 })
              reject(new Error(result.message || '上传失败'))
              return
            }

            let { fileUrl, fileName, fileSize } = result.data || {}

            if (fileUrl) {
              fileUrl = STATIC_URL + fileUrl.replace(/\\/g, '/')
            }

            updateFileStatus(index, {
              status: 'done',
              progress: 100,
              fileId: fileUrl,
              url: fileUrl,
              fileName: fileName || fileItem.name,
              fileSize: fileSize || fileItem.fileSize
            })

            resolve(result.data)
          } catch (e) {
            console.log(e)
            updateFileStatus(index, { status: 'error', progress: 0 })
            reject(new Error('解析响应失败'))
          }
        } else {
          updateFileStatus(index, { status: 'error', progress: 0 })
          reject(new Error(`HTTP ${xhr.status}`))
        }
      }

      xhr.onerror = () => {
        updateFileStatus(index, { status: 'error', progress: 0 })
        reject(new Error('网络错误'))
      }

      xhr.onabort = () => {
        updateFileStatus(index, { status: 'error', progress: 0 })
        reject(new Error('上传已取消'))
      }

      xhr.open('POST', '/apis/Coze/upload', true)
      xhr.setRequestHeader(tokenName, getToken())
      xhr.send(formData)
    })
  }

  const updateFileStatus = (index, updates) => {
    if (index < 0 || index >= selectedFiles.value.length) return
    selectedFiles.value[index] = { ...selectedFiles.value[index], ...updates }
    selectedFiles.value = [...selectedFiles.value]
  }

  const retryUpload = (index) => {
    const file = selectedFiles.value[index]
    if (!file) return
    uploadFileWithProgress(file, index)
  }

  const removeFile = (index) => {
    const file = selectedFiles.value[index]
    if (file?.xhr && file.status === 'uploading') {
      file.xhr.abort()
    }
    selectedFiles.value.splice(index, 1)
  }

  const handleKeydown = (e) => {
    if (e.key === 'Enter' && e.shiftKey) return true
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const autoResizeTextarea = () => {
    const textarea = inputRef.value
    if (!textarea) return
    textarea.style.height = 'auto'
    let height = Math.max(30, Math.min(textarea.scrollHeight, 100))
    textarea.style.height = `${height}px`
  }

  watch(inputMessage, () => {
    autoResizeTextarea()
  })

  const cozeSessionId = computed(() => store.state.cozeSessionId)

  function renderMarkdown(text, showCursor = false) {
    let cursorBlink = ''
    if (showCursor) {
      cursorBlink = '<span class="cursor-blink">▌</span>'
    }
    if (!text) return cursorBlink
    text = text + cursorBlink
    return marked.parse(text, { breaks: true })
  }

  const checkContentOverflow = () => {
    const el = messageListRef.value
    if (!el) return false
    return el.scrollHeight > el.clientHeight
  }

  const sendMessage = async () => {
    const query = inputMessage.value.trim()
    const hasFiles = selectedFiles.value.length > 0

    if ((!query && !hasFiles) || streaming.value) return

    if (hasUploading.value) {
      notice('错误', '请等待文件上传完成', 'error')
      return
    }

    let filePrompts = []
    if (hasFiles) {
      for (const file of selectedFiles.value) {
        if (file.fileId) {
          filePrompts.push({
            type: "upload_file",
            content: {
              upload_file: {
                file_name: file.fileName || file.name,
                url: file.fileId,
                file_path: ""
              }
            }
          })
        }
      }
    }

    const currentPrompt = []
    if (query) {
      currentPrompt.push({
        type: "text",
        content: { text: query }
      })
    }
    if (filePrompts.length) {
      currentPrompt.push(...filePrompts)
    }

    messages.value.push({
      role: 'user',
      text: query,
      attachments: selectedFiles.value.map(f => ({
        url: f.url,
        fileName: f.fileName || f.name,
        fileSize: f.fileSize,
        fileType: f.fileType
      }))
    })

    inputMessage.value = ''
    selectedFiles.value = []
    streaming.value = true
    streamContent.value = ''
    hasAutoScrolled.value = false
    isUserScrolledUp.value = false

    await scrollToBottom(true)

    const userQuery = {
      query: {
        prompt: currentPrompt
      },
      session_id: cozeSessionId.value
    }

    currentStream = createCozeStream('/apis/Coze/stream', { ...userQuery }, {
      onSessionId: (sid) => {
        store.commit("setCozeSessionId", sid)
      },
      onMessage: (chunk, fullText) => {
        streamContent.value = fullText
        const isOverflow = checkContentOverflow()
        if (isUserScrolledUp.value) return
        if (isOverflow && !hasAutoScrolled.value) {
          scrollToBottom(true)
          hasAutoScrolled.value = true
        } else if (!isOverflow) {
          scrollToBottom(true)
        }
      },
      onComplete: () => {
        if (streamContent.value) {
          messages.value.push({
            role: 'assistant',
            content: streamContent.value
          })
        }
        resetStream()
        nextTick(() => {
          handleScroll()
        })
      },
      onAuthError: (msg) => {
        doReLogin(msg)
        resetStream()
      },
      onError: (error) => {
        console.log(error)
        messages.value.push({
          role: 'assistant',
          content: error || '请求失败，请重试'
        })
        resetStream()
      }
    })
  }

  const stopStream = () => {
    if (currentStream) {
      currentStream.close()
      messages.value.push({
        role: 'assistant',
        content: streamContent.value || '已停止生成'
      })
      resetStream()
    }
  }

  const resetStream = () => {
    streaming.value = false
    streamContent.value = ''
    currentStream = null
    hasAutoScrolled.value = false
    isUserScrolledUp.value = false
  }

  const scrollToBottom = async (force = false) => {
    await nextTick()
    if (messageListRef.value) {
      messageListRef.value.scrollTo({
        top: messageListRef.value.scrollHeight,
        behavior: 'smooth'
      })
      if (force) {
        showScrollToBottom.value = false
      }
    }
  }
</script>

<style lang="less" scoped>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  :deep(a){
    color: @themeColor !important;
  }
  .chat-float-box {
    position: fixed;
    bottom: 0px;
    right: 0px;
    width: 60vw;
    height: 85vh;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    background: #fff;
    overflow: hidden;
    z-index: 1999;
    display: none;
    flex-direction: column;
  }
  .chat-float-box.show {
    display: flex;
  }
  .chat-header {
    padding: 14px 20px;
    background: linear-gradient(135deg, #165dff, #36bffa);
    color: #fff;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .close-btn {
    background: none;
    border: none;
    font-size: 25px;
    cursor: pointer;
    color: #fff;
    margin-top: -4px;
    &:hover { color: yellow; }
  }
  .chat-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    scroll-behavior: smooth;
  }
  .scroll-to-bottom-btn {
    position: absolute;
    top: -50px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: opacity .3s ease-in-out;
    z-index: 10;
    &:hover {
      color: #165dff;
      transform: translateX(-50%) translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    &:active {
      transform: translateX(-50%) translateY(0);
    }
    &.loading {
      color: #165dff;
      border-color: #165dff;
      &:hover {
        color: #165dff;
        transform: translateX(-50%) translateY(-1px);
        box-shadow: 0 4px 14px rgba(22, 93, 255, 0.3);
      }
    }
  }
  .arrow-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    border-radius: 100%;
  }
  .down-arrow {
    position: relative;
    z-index: 2;
    color: #333;
    transition: color 0.2s ease;
  }
  .arrow-glow {
    position: absolute;
    right: -2px;
    top: 50%;
    transform: translateY(-50%);
    width: 12px;
    height: 24px;
    border-radius: 50%;
    filter: blur(4px);
    opacity: 0.7;
    animation: glowPulse 2s ease-in-out infinite;
    z-index: 1;
  }
  @keyframes glowPulse {
    0%, 100% { opacity: 0.5; filter: blur(3px); }
    50% { opacity: 0.9; filter: blur(5px); }
  }
  .loading-ring-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .glow-ring {
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    border: 2px solid transparent;
    border-top-color: #165dff;
    animation: glowRingRotate 1s linear infinite;
    box-shadow: 0 0 8px rgba(22, 93, 255, 0.3), 0 0 16px rgba(54, 191, 250, 0.2), inset 0 0 8px rgba(22, 93, 255, 0.1);
  }
  @keyframes glowRingRotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .message {
    display: flex;
    align-items: baseline;
    margin-bottom: 16px;
    gap: 10px;
    &.user { flex-direction: row-reverse !important; }
    .avatar {
      font-size: 1.2rem;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #bddbfb;
      color: #797979;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .msg-text {
      line-height: 1.6;
      :deep(p) { margin: 0 0 8px 0; }
      :deep(p:last-child) { margin-bottom: 0; }
      :deep(pre) {
        background: #1e1e1e;
        color: #d4d4d4;
        padding: 12px;
        border-radius: 8px;
        overflow-x: auto;
        margin: 8px 0;
      }
      :deep(code) {
        font-family: 'Fira Code', monospace;
        font-size: 13px;
      }
    } 
  }
  .message.user .avatar { background: #cdffb4 !important; }
  .message.user .msg-text {
    padding: 5px 10px;
    border-radius: 5px;
    text-align: right;
  }
  .msg-content {
    line-height: 1.6;
    max-width: 70%;
  }
  .attachment-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    .attachment-item {
      cursor: pointer;
      transition: transform 0.2s;
      &:hover { transform: scale(1.02); }
      &.image {
        width: 80px;
        height: 80px;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #e5e7eb;
        img { width: 100%; height: 100%; object-fit: cover; }
      }
      &.file {
        .file-card {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: #f5f7fa;
          border-radius: 8px;
          border: 1px solid #e4e7ed;
          min-width: 200px;
          height: 100%;
          box-sizing: border-box;
          .file-icon { color: #909399; flex-shrink: 0; }
          .file-info {
            flex: 1;
            min-width: 0;
            .file-name {
              font-size: 13px;
              color: #303133;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .file-size {
              font-size: 11px;
              color: #909399;
              margin-top: 2px;
            }
          }
        }
      }
    }
  }
  .selected-files {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding-bottom: 4px;
    .selected-file-item {
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid #e5e7eb;
      transition: all 0.2s;
      &.image {
        width: 80px;
        height: 80px;
        img { width: 100%; height: 100%; object-fit: cover; display: block; }
      }
      &.file { min-width: 200px; max-width: 280px; }
      &.placeholder { background: #f5f7fa; }
      .file-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 16px 20px;
        gap: 6px;
        .placeholder-icon { color: #c0c4cc; }
        .placeholder-name {
          font-size: 12px;
          color: #606266;
          max-width: 150px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .placeholder-hint { font-size: 11px; color: #c0c4cc; }
      }
      .file-card {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        background: #f5f7fa;
        min-width: 200px;
        height: 100%;
        box-sizing: border-box;
        .file-icon { color: #909399; flex-shrink: 0; }
        .file-info {
          flex: 1;
          min-width: 0;
          .file-name {
            font-size: 13px;
            color: #303133;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .file-size {
            font-size: 11px;
            color: #909399;
            margin-top: 2px;
          }
          .file-progress-bar {
            width: 100%;
            height: 4px;
            background: #e4e7ed;
            border-radius: 2px;
            margin-top: 6px;
            overflow: hidden;
            .file-progress-fill {
              height: 100%;
              background: linear-gradient(90deg, #165dff, #36bffa);
              border-radius: 2px;
              transition: width 0.3s ease;
            }
          }
          .file-progress-text {
            font-size: 11px;
            color: #165dff;
            margin-top: 4px;
          }
        }
      }
      .upload-overlay {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 7px;
        &.error { background: rgba(0, 0, 0, 0.2); }
        .progress-ring {
          position: relative;
          width: 32px;
          height: 32px;
          svg {
            transform: rotate(-90deg);
            width: 100%;
            height: 100%;
            .bg {
              fill: none;
              stroke: rgba(255,255,255,0.2);
              stroke-width: 3;
            }
            .progress {
              fill: none;
              stroke: #fff;
              stroke-width: 3;
              stroke-linecap: round;
              transition: stroke-dasharray 0.3s ease;
            }
          }
          .progress-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: #fff;
            font-size: 10px;
            font-weight: 600;
          }
        }
        .error-text {
          color: #dc2626;
          font-size: 11px;
          font-weight: 500;
        }
        .retry-btn {
          margin-top: 4px;
          padding: 2px 8px;
          border: none;
          border-radius: 4px;
          background: #fff;
          color: #dc2626;
          font-size: 11px;
          cursor: pointer;
          &:hover { background: #fff7f7; }
        }
      }
      .remove-btn {
        position: absolute;
        top: 1px;
        right: 1px;
        width: 16px;
        height: 16px;
        background: #c3c3c3;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        cursor: pointer;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.2s ease, visibility 0.2s ease;
        z-index: 10;
        line-height: 1;
      }
      &:hover .remove-btn {
        opacity: 1;
        visibility: visible;
      }      
      &.uploading .remove-btn,
      &.error .remove-btn,
      &.placeholder .remove-btn {
        opacity: 1;
        visibility: visible;
        background: rgba(0,0,0,0.5);
      }
    }
  }
  .chat-input-area {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    border-top: 1px solid #eee;
    gap: 8px;
    .chat-editor{
      border: 2px solid #eee;
      border-radius: 10px;
      padding: 10px;
    }
    .input-row {
      display: flex;
      gap: 10px;
      align-items: flex-end;
    }
    .chat-editor-action{
      display: flex;
      flex: 1;
      justify-content: space-between;
      margin-top: 5px;
    }
    .upload-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: none;
      border: 1px solid #ccc;
      background: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.2s;
      &:hover { background: #f9f9f9; }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .plus-icon {
        font-size: 22px;
        color: #6b7280;
      }
    }
    .textarea-wrapper {
      flex: 1;
      background: #fff;
    }
    textarea {
      outline: none;
      border: none;
      font-size: 14px;
      line-height: 1.5;
      width: 100%;
      height: 30px;
      min-height: 30px;
      max-height: 100px;
      resize: none;
      overflow-y: auto;
      box-sizing: border-box;
      padding: 4px;
      background: transparent;
    }
    .svg-icon {
      cursor: pointer;
      width: 33px;
      height: 33px;
      fill: #333;
      flex-shrink: 0;
      &[disabled="true"] {
        fill: #ffffe6;
        cursor: not-allowed;
        border-radius: 50%;
        background-color: #D9D9D9;
      }
      &[disabled="false"] {
        fill: #ffffff;
        cursor: pointer;
        border-radius: 50%;
        background-color: #1F78FE;
      }
    }
  }
  :deep(.cursor-blink) {
    display: inline;
    color: #333;
    margin-left: 3px;
    animation: cursorBlink 1s infinite;
    vertical-align: baseline;
    white-space: nowrap;
  }
  @keyframes cursorBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
</style>